import React, { useCallback, useRef } from 'react'
import { useToast } from '@sketch/toasts'
import { ProjectFragment } from '@sketch/gql-types'

import { MovedToProjectToast } from '../../../storageItems/components/MovedToasts'
import {
  StorageItemIdentity,
  useTransferStorageItems,
} from '../../../storageItems'

interface UseTransferShareProps {
  workspaceId: string
  newProject: ProjectFragment
}

/**
 * Hook to transfer a storage item (share or folder) to a new folder
 */
export const useTransfer = (props: UseTransferShareProps) => {
  const { showToast } = useToast()
  const propsRef = useRef(props)
  propsRef.current = props

  const [moveRaw, { loading }] = useTransferStorageItems({
    onError: errorMessage => {
      showToast(errorMessage, 'negative')
    },
  })

  const move = useCallback(
    (items: StorageItemIdentity[], onCompleted?: () => void) => {
      const { newProject, workspaceId } = propsRef.current

      const [item] = items
      const type =
        items.length > 1
          ? 'Selection'
          : item.__typename === 'Share'
            ? 'Document'
            : 'Folder'

      return moveRaw({
        onCompleted: () => {
          showToast(
            <MovedToProjectToast
              project={newProject}
              type={type}
              workspaceId={workspaceId}
            />
          )
          onCompleted?.()
        },
        items,
        location: {
          source: { workspaceId },
          destination: {
            workspaceId,
            projectId: newProject.identifier,
          },
        },
      })
    },
    [moveRaw, showToast]
  )

  return { move, loading }
}
